import { create } from 'zustand';
import { api, integrationsApi } from '../lib/api';

// In-flight Apollo people search — module-level (not store state) because an
// AbortController isn't renderable data. One search runs at a time.
let apolloSearchInFlight: { requestId: string; controller: AbortController } | null = null;

const newRequestId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/** One reason an enrichment run fell short, as the API classifies it. */
export interface EnrichmentProblem {
  provider: string;
  status: string;
  /** auth, missing_key, plan_blocked, rate_limit, unavailable, missing_input,
   *  no_match, timeout, unknown. Branch on this, never on `error`. */
  error_kind: string;
  error?: string | null;
  /** The sentence to show a user. */
  hint: string;
  /** True when the user can fix it themselves, so it leads. */
  actionable: boolean;
  /** False for "nothing to search with" and plan limits: information, not a fault. */
  is_problem?: boolean;
}

export interface Contact {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  job_title?: string;
  department?: string;
  seniority_level?: string;
  company_name?: string;
  company_website?: string;
  company_industry?: string;
  company_size?: string;
  company_revenue?: string;
  phone?: string;
  linkedin_url?: string;
  twitter_url?: string;
  city?: string;
  state?: string;
  country?: string;
  timezone?: string;
  tags?: string[];
  segments?: string[];
  lead_score?: number;
  fit_score?: number;
  intent_score?: number;
  score_breakdown?: any;
  lead_status?: string;
  email_status?: string;
  total_emails_sent: number;
  total_emails_opened: number;
  total_emails_clicked: number;
  total_emails_replied: number;
  last_email_sent?: string;
  unsubscribed_at?: string;
  import_source?: string;
  created_at?: string;
  updated_at?: string;
  research_data?: any;
  enrichment_data?: any;
  enrichment_status?: string;
  enrichment_provider?: string;
  enrichment_model?: string;
  last_enriched_at?: string;
  enrichment_results?: Record<string, any>;
  /** Why the last enrichment run did not fully succeed, already classified by
   *  the API: one entry per provider that did not complete, plus the run itself
   *  when it never finished. Absent when the run was clean. The server owns the
   *  classification, so `hint` is ready to show and the UI never has to parse a
   *  vendor's wording. */
  enrichment_problems?: EnrichmentProblem[];
  // Soft delete: deleted contacts are hidden everywhere except campaign history
  is_deleted?: boolean;
  deleted_at?: string | null;
}

/** A Representative's delete comes back as a request for approval, not a deletion. */
export interface PendingDeletionResult {
  status: 'pending_approval'
  request_id: number
  message: string
}

export interface BulkDeleteResult {
  message?: string;
  deleted_count?: number;
  // Contacts skipped because they are in active (scheduled/running/paused)
  // campaigns, with the blocking campaign names.
  skipped?: { id: number; name: string; campaigns: string[] }[];
}

// Outcome of cancelling an Apollo search. See `cancelApolloSearch`.
export interface ApolloCancelResult {
  aborted: boolean;
  serverConfirmed: boolean | null;
}

export interface ContactList {
  id: number;
  name: string;
  description?: string;
  contact_ids: number[];
  total_contacts: number;
  active_contacts: number;
  created_at: string;
  updated_at: string;
  // Transient (create/update response only): how many selected contacts were
  // skipped because their email is invalid/fake and can't feed campaigns.
  skipped_invalid?: number;
}

export interface ImportHistory {
  id: number;
  file_name: string;
  import_type: string;
  total_rows: number;
  successful_imports: number;
  failed_imports: number;
  duplicate_emails: number;
  status: string;
  error_log?: any;
  created_at: string;
  completed_at?: string;
  // Present when the import targeted a contact list (created or appended).
  list_id?: number | null;
  list_name?: string | null;
  added_to_list?: number | null;
}

export interface TagInfo {
  id?: number;      // present when the tag is registered in the Tag table
  name: string;
  count: number;
}

export interface ContactFilters {
  search?: string;
  // Per-column grid filters (case-insensitive contains). name -> first/last/full
  // name, company -> company_name, location -> city/state/country.
  name?: string;
  company?: string;
  location?: string;
  lead_status?: string;
  email_status?: string;
  tags?: string;
  segments?: string;
  list_id?: number;
  // Comma-separated list ids — filters to the union of their members (the
  // Filters dropdown allows selecting several lists at once).
  list_ids?: string;
  // Last-modified date range (YYYY-MM-DD), filtered server-side on updated_at
  // (falling back to created_at for never-updated contacts).
  updated_after?: string;
  updated_before?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

interface ContactStore {
  // State
  contacts: Contact[];
  selectedContacts: number[];
  contactLists: ContactList[];
  importHistory: ImportHistory[];
  currentContact: Contact | null;
  filters: ContactFilters;
  pagination: {
    skip: number;
    limit: number;
    total: number;
  };
  // Audience overview aggregates over the whole filtered set (not just the page)
  contactStats: {
    total: number;
    active: number;
    engaged: number;
    qualified: number;
  };
  loading: boolean;
  error: string | null;
  allTags: TagInfo[];
  // Single active tag — kept in sync with activeTags for single-select consumers
  // (standalone Tags dropdown, TagSidebar, BulkActionsBar). Null when 0 or >1
  // tags are active.
  activeTag: string | null;
  // Full set of active tag filters (the Filters dropdown allows multiple). This
  // is the source of truth; filters.tags is derived from it (comma-separated).
  activeTags: string[];

  // Actions
  fetchContacts: () => Promise<void>;
  fetchContactCount: () => Promise<void>;
  fetchTags: () => Promise<void>;
  createTag: (name: string) => Promise<void>;
  deleteTag: (name: string) => Promise<void>;
  setActiveTag: (tag: string | null) => void;
  setActiveTags: (tags: string[]) => void;
  fetchContact: (id: number) => Promise<void>;
  createContact: (data: Partial<Contact>) => Promise<Contact>;
  updateContact: (id: number, data: Partial<Contact>) => Promise<Contact>;
  deleteContact: (id: number) => Promise<PendingDeletionResult | void>;
  setFilters: (filters: ContactFilters) => void;
  setPagination: (pagination: Partial<ContactStore['pagination']>) => void;
  selectContact: (id: number) => void;
  deselectContact: (id: number) => void;
  selectAllContacts: () => void;
  selectContacts: (ids: number[]) => void;
  deselectAllContacts: () => void;
  pruneSelection: (visibleIds: number[]) => void;
  bulkDelete: (ids: number[]) => Promise<BulkDeleteResult | PendingDeletionResult | undefined>;
  bulkTag: (ids: number[], tag: string) => Promise<void>;
  bulkUntag: (ids: number[], tag: string) => Promise<void>;
  bulkRemoveFromList: (ids: number[], listId: number) => Promise<void>;
  bulkUpdateStatus: (ids: number[], status: string) => Promise<void>;
  enrichContact: (id: number, provider: string, model: string) => Promise<any>;
  bulkEnrichContacts: (ids: number[], provider: string, model: string) => Promise<void>;

  // Apollo
  apolloResults: any[];
  apolloTotal: number;
  apolloPage: number;
  apolloLoading: boolean;
  apolloSearch: (filters: {
    query?: string;
    titles?: string[];
    include_similar_titles?: boolean;
    company_names?: string[];
    organization_ids?: string[];
    organization_domains?: string[];
    industries?: string[];
    locations?: string[];
    organization_locations?: string[];
    seniority_levels?: string[];
    employee_ranges?: string[];
    revenue_min?: number;
    revenue_max?: number;
    technologies?: string[];
    email_status?: string[];
    page?: number;
    per_page?: number;
    fetch_all?: boolean;
  }) => Promise<void>;
  // Cancels the in-flight Apollo search: flags the backend (which aborts its
  // outbound request to Apollo's API) and drops the pending browser request.
  //
  // `aborted` is whether there was a browser request to drop. `serverConfirmed`
  // is what the backend actually reported: true = the sweep was found and
  // stopped, false = the backend had no record of it (a `fetch_all` sweep
  // running in another worker process keeps going, and keeps spending credits),
  // null = the cancel call itself failed, so nothing is known. Callers must not
  // claim the search stopped unless this is true.
  cancelApolloSearch: () => Promise<ApolloCancelResult>;
  apolloImport: (people: any[], tags?: string[], listId?: number) => Promise<{ imported: number; duplicates: number; errors: number; list_id: number | null }>;
  clearApolloResults: () => void;

  // Hunter
  hunterResults: any[];
  hunterTotal: number;
  hunterOffset: number;
  hunterLoading: boolean;
  hunterDomainSearch: (filters: {
    domain: string;
    limit?: number;
    offset?: number;
    type?: string;
    seniority?: string[];
    department?: string[];
  }) => Promise<void>;
  hunterImport: (people: any[], tag?: string) => Promise<{ imported: number; duplicates: number; errors: number }>;
  clearHunterResults: () => void;

  // Zoho CRM
  zohoImport: (people: any[], tags?: string[], listId?: number) => Promise<{ imported: number; duplicates: number; errors: number; list_id: number | null }>;

  // Generic CRM import (HubSpot / Salesforce / Pipedrive)
  crmImport: (provider: string, people: any[], tags?: string[], listId?: number) => Promise<{ imported: number; duplicates: number; errors: number; list_id: number | null; added_to_list: number | null }>;

  // Import
  importContacts: (file: File, options?: { skip_duplicates?: boolean; update_duplicates?: boolean; column_mapping?: Record<string, string>; list_id?: number; new_list_name?: string }) => Promise<ImportHistory>;
  fetchImportHistory: () => Promise<void>;
  downloadTemplate: (format: 'csv' | 'xlsx') => Promise<void>;

  // Lists
  fetchContactLists: () => Promise<void>;
  createContactList: (data: { name: string; description?: string; contact_ids: number[] }) => Promise<ContactList>;
  updateContactList: (id: number, data: { name: string; description?: string; contact_ids: number[] }) => Promise<ContactList>;
  deleteContactList: (id: number) => Promise<PendingDeletionResult | void>;

  // Reset
  reset: () => void;
}

const initialState = {
  contacts: [],
  selectedContacts: [],
  contactLists: [],
  importHistory: [],
  currentContact: null,
  filters: {},
  pagination: {
    skip: 0,
    limit: 50,
    total: 0,
  },
  contactStats: {
    total: 0,
    active: 0,
    engaged: 0,
    qualified: 0,
  },
  loading: false,
  error: null,
  allTags: [] as TagInfo[],
  activeTag: null as string | null,
  activeTags: [] as string[],
  apolloResults: [] as any[],
  apolloTotal: 0,
  apolloPage: 1,
  apolloLoading: false,
  hunterResults: [] as any[],
  hunterTotal: 0,
  hunterOffset: 0,
  hunterLoading: false,
};

export const useContactStore = create<ContactStore>((set, get) => ({
  ...initialState,

  fetchContacts: async () => {
    set({ loading: true, error: null });
    try {
      const { filters, pagination } = get();
      const params = new URLSearchParams({
        skip: pagination.skip.toString(),
        limit: pagination.limit.toString(),
        ...(filters.search && { search: filters.search }),
        ...(filters.name && { name: filters.name }),
        ...(filters.company && { company: filters.company }),
        ...(filters.location && { location: filters.location }),
        ...(filters.lead_status && { lead_status: filters.lead_status }),
        ...(filters.email_status && { email_status: filters.email_status }),
        ...(filters.tags && { tags: filters.tags }),
        ...(filters.segments && { segments: filters.segments }),
        ...(filters.list_id != null && { list_id: filters.list_id.toString() }),
        ...(filters.list_ids && { list_ids: filters.list_ids }),
        ...(filters.updated_after && { updated_after: filters.updated_after }),
        ...(filters.updated_before && { updated_before: filters.updated_before }),
        ...(filters.sort_by && { sort_by: filters.sort_by }),
        ...(filters.sort_order && { sort_order: filters.sort_order }),
      });

      const response = await api.get(`/contacts/?${params.toString()}`);
      set({ contacts: response.data, loading: false });
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to fetch contacts', loading: false });
    }
  },

  fetchContactCount: async () => {
    try {
      const { filters } = get();
      const params = new URLSearchParams({
        ...(filters.search && { search: filters.search }),
        ...(filters.name && { name: filters.name }),
        ...(filters.company && { company: filters.company }),
        ...(filters.location && { location: filters.location }),
        ...(filters.lead_status && { lead_status: filters.lead_status }),
        ...(filters.email_status && { email_status: filters.email_status }),
        ...(filters.list_id != null && { list_id: filters.list_id.toString() }),
        ...(filters.list_ids && { list_ids: filters.list_ids }),
        ...(filters.updated_after && { updated_after: filters.updated_after }),
        ...(filters.updated_before && { updated_before: filters.updated_before }),
      });

      // /stats applies the same filters and returns the audience-wide
      // aggregates, so the overview tiles match the table count instead of
      // only reflecting the current page.
      const response = await api.get(`/contacts/stats?${params.toString()}`);
      const { total, active, engaged, qualified } = response.data;
      set((state) => ({
        pagination: { ...state.pagination, total },
        contactStats: { total, active, engaged, qualified },
      }));
    } catch (error) {
      console.error('Failed to fetch contact stats:', error);
    }
  },

  fetchTags: async () => {
    try {
      const response = await api.get('/contacts/tags');
      set({ allTags: response.data });
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  },

  createTag: async (name: string) => {
    await api.post('/contacts/tags', { name: name.trim() });
    await get().fetchTags();
  },

  deleteTag: async (name: string) => {
    await api.delete('/contacts/tags', { params: { name } });
    // Drop the deleted tag from the active filter set if present.
    const active = get().activeTags;
    if (active.some((t) => t.toLowerCase() === name.toLowerCase())) {
      get().setActiveTags(active.filter((t) => t.toLowerCase() !== name.toLowerCase()));
    }
    await get().fetchTags();
    await get().fetchContacts();
    await get().fetchContactCount();
  },

  setActiveTag: (tag: string | null) => {
    // Single-select entry point (standalone dropdowns / sidebar): replaces the
    // whole tag selection with this one tag (or clears it).
    get().setActiveTags(tag ? [tag] : []);
  },

  setActiveTags: (tags: string[]) => {
    // Trim + dedupe, preserving pick order. filters.tags (comma-separated) is
    // the query the backend AND-combines; activeTag mirrors the single case.
    const clean: string[] = [];
    const seen = new Set<string>();
    for (const t of tags) {
      const v = (t || '').trim();
      if (v && !seen.has(v)) { seen.add(v); clean.push(v); }
    }
    set({ activeTags: clean, activeTag: clean.length === 1 ? clean[0] : null });
    get().setFilters({ tags: clean.length ? clean.join(',') : undefined });
  },

  fetchContact: async (id: number) => {
    set({ loading: true, error: null });
    try {
      const response = await api.get(`/contacts/${id}`);
      set({ currentContact: response.data, loading: false });
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to fetch contact', loading: false });
    }
  },

  createContact: async (data: Partial<Contact>) => {
    set({ loading: true, error: null });
    try {
      // skipErrorToast — AddContactModal renders its own error toast (with a
      // trial-aware message + Upgrade action on a plan-limit 403 via
      // planLimitToastMessage); letting the global interceptor toast too
      // would double it.
      const response = await api.post('/contacts/', data, { skipErrorToast: true } as any);
      set((state) => ({
        contacts: [response.data, ...state.contacts],
        loading: false,
      }));
      await get().fetchContactCount();
      return response.data;
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to create contact', loading: false });
      throw error;
    }
  },

  updateContact: async (id: number, data: Partial<Contact>) => {
    set({ loading: true, error: null });
    try {
      // skipErrorToast — see createContact above; AddContactModal's catch
      // block handles both add and edit, so both must skip the interceptor.
      const response = await api.put(`/contacts/${id}`, data, { skipErrorToast: true } as any);
      set((state) => ({
        contacts: state.contacts.map((c) => (c.id === id ? response.data : c)),
        currentContact: state.currentContact?.id === id ? response.data : state.currentContact,
        loading: false,
      }));
      return response.data;
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to update contact', loading: false });
      throw error;
    }
  },

  deleteContact: async (id: number) => {
    set({ loading: true, error: null });
    try {
      // skipErrorToast: the modal renders a styled warning naming the
      // blocking campaigns instead of the interceptor's generic red toast.
      const response = await api.delete(`/contacts/${id}`, { skipErrorToast: true } as any);
      if (response.data?.status === 'pending_approval') {
        // A Representative's delete is a request: nothing was deleted, so
        // the contact stays exactly where it is.
        set({ loading: false });
        return response.data as PendingDeletionResult;
      }
      set((state) => ({
        contacts: state.contacts.filter((c) => c.id !== id),
        selectedContacts: state.selectedContacts.filter((cid) => cid !== id),
        loading: false,
      }));
      await get().fetchContactCount();
      await get().fetchTags();
      // Refresh list member counts (list.total_contacts) so the Lists picker
      // reflects the deletion without a page reload.
      await get().fetchContactLists();
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to delete contact', loading: false });
      throw error;
    }
  },

  setFilters: (filters: ContactFilters) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
      pagination: { ...state.pagination, skip: 0 }, // Reset to first page
    }));
    get().fetchContacts();
    get().fetchContactCount();
  },

  setPagination: (pagination: Partial<ContactStore['pagination']>) => {
    set((state) => ({
      pagination: { ...state.pagination, ...pagination },
    }));
    get().fetchContacts();
  },

  selectContact: (id: number) => {
    set((state) => ({
      selectedContacts: [...state.selectedContacts, id],
    }));
  },

  deselectContact: (id: number) => {
    set((state) => ({
      selectedContacts: state.selectedContacts.filter((cid) => cid !== id),
    }));
  },

  selectAllContacts: () => {
    set((state) => ({
      selectedContacts: state.contacts.map((c) => c.id),
    }));
  },

  // Replaces the selection with exactly `ids`. Used by the header checkbox so
  // "select all" means "the rows you can actually see" — never the unloaded
  // remainder of a server page, which bulk actions would silently skip.
  selectContacts: (ids: number[]) => {
    set({ selectedContacts: Array.from(new Set(ids)) });
  },

  deselectAllContacts: () => {
    set({ selectedContacts: [] });
  },

  // Drops any selected id that is no longer visible. Without this, a selection
  // survives a filter/search/page change and a bulk action then mutates rows
  // the user can no longer see.
  pruneSelection: (visibleIds: number[]) => {
    set((state) => {
      const allowed = new Set(visibleIds);
      const next = state.selectedContacts.filter((id) => allowed.has(id));
      return next.length === state.selectedContacts.length ? state : { selectedContacts: next };
    });
  },

  bulkDelete: async (ids: number[]) => {
    set({ loading: true, error: null });
    try {
      const response = await api.post('/contacts/bulk-action', {
        contact_ids: ids,
        action: 'delete',
      });
      if (response.data?.status === 'pending_approval') {
        // A Representative's delete is a request: nothing was deleted, so the
        // list is left as is (selection cleared, the action is done).
        set({ selectedContacts: [], loading: false });
        return response.data as PendingDeletionResult;
      }
      set({ selectedContacts: [], loading: false });
      // Refetch instead of filtering locally: contacts in active campaigns
      // are skipped server-side and must stay visible.
      await get().fetchContacts();
      await get().fetchContactCount();
      await get().fetchTags();
      // Refresh list member counts (list.total_contacts) so the Lists picker
      // reflects the deletions without a page reload.
      await get().fetchContactLists();
      return response.data as BulkDeleteResult | undefined;
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to delete contacts', loading: false });
      throw error;
    }
  },

  bulkTag: async (ids: number[], tag: string) => {
    set({ loading: true, error: null });
    try {
      await api.post('/contacts/bulk-action', {
        contact_ids: ids,
        action: 'tag',
        data: { tag },
      });
      await get().fetchContacts();
      await get().fetchTags();
      // Clear the selection once the action completes (matches bulkDelete).
      set({ loading: false, selectedContacts: [] });
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to tag contacts', loading: false });
      throw error;
    }
  },

  bulkUntag: async (ids: number[], tag: string) => {
    set({ loading: true, error: null });
    try {
      await api.post('/contacts/bulk-action', {
        contact_ids: ids,
        action: 'untag',
        data: { tag },
      });
      await get().fetchContacts();
      await get().fetchTags();
      set({ loading: false, selectedContacts: [] });
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to untag contacts', loading: false });
      throw error;
    }
  },

  bulkRemoveFromList: async (ids: number[], listId: number) => {
    set({ loading: true, error: null });
    try {
      await api.post('/contacts/bulk-action', {
        contact_ids: ids,
        action: 'remove_from_list',
        data: { list_id: listId },
      });
      // Refresh list membership counts, and if we're currently viewing that
      // list, refresh the visible rows too (the removed ones should drop off).
      await get().fetchContactLists();
      const f = get().filters;
      const viewingList =
        f.list_id === listId ||
        (f.list_ids || '').split(',').map((s) => s.trim()).includes(String(listId));
      if (viewingList) {
        await get().fetchContacts();
        await get().fetchContactCount();
      }
      set({ loading: false, selectedContacts: [] });
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to remove from list', loading: false });
      throw error;
    }
  },

  bulkUpdateStatus: async (ids: number[], status: string) => {
    set({ loading: true, error: null });
    try {
      await api.post('/contacts/bulk-action', {
        contact_ids: ids,
        action: 'update_status',
        data: { status },
      });
      await get().fetchContacts();
      set({ loading: false, selectedContacts: [] });
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to update status', loading: false });
      throw error;
    }
  },

  enrichContact: async (id: number, provider: string, model: string) => {
    try {
      const response = await api.post(`/enrichment/${id}`, { provider, model });
      set((state) => ({
        contacts: state.contacts.map((c) =>
          c.id === id
            ? {
                ...c,
                enrichment_data: response.data.enrichment_data,
                enrichment_status: 'completed',
                enrichment_provider: provider,
                enrichment_model: model,
              }
            : c
        ),
      }));
      return response.data;
    } catch (error: any) {
      throw error;
    }
  },

  bulkEnrichContacts: async (ids: number[], provider: string, model: string) => {
    try {
      await api.post('/enrichment/bulk', {
        contact_ids: ids,
        provider,
        model,
      });
      // Mark contacts as in_progress locally and clear the selection.
      set((state) => ({
        contacts: state.contacts.map((c) =>
          ids.includes(c.id) ? { ...c, enrichment_status: 'in_progress' } : c
        ),
        selectedContacts: [],
      }));
    } catch (error: any) {
      throw error;
    }
  },

  apolloSearch: async (filters) => {
    const requestId = newRequestId();
    const controller = new AbortController();
    apolloSearchInFlight = { requestId, controller };
    set({ apolloLoading: true });
    try {
      const response = await integrationsApi.apolloSearch(
        { ...filters, request_id: requestId },
        controller.signal
      );
      set({
        apolloResults: response.data.people || [],
        apolloTotal: response.data.total || 0,
        apolloPage: response.data.page || 1,
        apolloLoading: false,
      });
    } catch (error: any) {
      set({ apolloLoading: false });
      throw error;
    } finally {
      if (apolloSearchInFlight?.requestId === requestId) apolloSearchInFlight = null;
    }
  },

  cancelApolloSearch: async () => {
    const inflight = apolloSearchInFlight;
    if (!inflight) return { aborted: false, serverConfirmed: null };
    apolloSearchInFlight = null;
    // Flag the backend first — it cancels the server-side task, aborting the
    // outbound request to Apollo's API — then drop our own pending request.
    const backendCancel = integrationsApi
      .apolloSearchCancel(inflight.requestId)
      .then((res) => (res?.data?.canceled === true ? true : false))
      .catch(() => null); // network/HTTP failure — the sweep's fate is unknown
    inflight.controller.abort();
    set({ apolloLoading: false });
    const serverConfirmed = await backendCancel;
    return { aborted: true, serverConfirmed };
  },

  apolloImport: async (people, tags, listId) => {
    set({ apolloLoading: true });
    try {
      const response = await integrationsApi.apolloImport({ people, tags, list_id: listId });
      set({ apolloLoading: false });
      // Refresh contacts after import
      await get().fetchContacts();
      await get().fetchContactCount();
      await get().fetchTags();
      // If contacts were appended to a list, refresh the lists too
      if (listId !== undefined) await get().fetchContactLists();
      return response.data;
    } catch (error: any) {
      set({ apolloLoading: false });
      throw error;
    }
  },

  clearApolloResults: () => {
    set({ apolloResults: [], apolloTotal: 0, apolloPage: 1 });
  },

  hunterDomainSearch: async (filters) => {
    set({ hunterLoading: true });
    try {
      const response = await integrationsApi.hunterDomainSearch(filters);
      set({
        hunterResults: response.data.emails || [],
        hunterTotal: response.data.total || 0,
        hunterOffset: response.data.offset || 0,
        hunterLoading: false,
      });
    } catch (error: any) {
      set({ hunterLoading: false });
      throw error;
    }
  },

  hunterImport: async (people, tag) => {
    set({ hunterLoading: true });
    try {
      const response = await integrationsApi.hunterImport({ people, tag });
      set({ hunterLoading: false });
      await get().fetchContacts();
      await get().fetchContactCount();
      await get().fetchTags();
      return response.data;
    } catch (error: any) {
      set({ hunterLoading: false });
      throw error;
    }
  },

  clearHunterResults: () => {
    set({ hunterResults: [], hunterTotal: 0, hunterOffset: 0 });
  },

  zohoImport: async (people, tags, listId) => {
    const response = await integrationsApi.zohoImport({ people, tags, list_id: listId });
    // Refresh contacts after import
    await get().fetchContacts();
    await get().fetchContactCount();
    await get().fetchTags();
    // If contacts were appended to a list, refresh the lists too
    if (listId !== undefined) await get().fetchContactLists();
    return response.data;
  },

  crmImport: async (provider, people, tags, listId) => {
    const response = await integrationsApi.crmImport({ provider, people, tags, list_id: listId });
    // Refresh contacts after import (mirrors zohoImport)
    await get().fetchContacts();
    await get().fetchContactCount();
    await get().fetchTags();
    if (listId !== undefined) await get().fetchContactLists();
    return response.data;
  },

  importContacts: async (file: File, options = {}) => {
    set({ loading: true, error: null });
    try {
      const formData = new FormData();
      formData.append('file', file);

      const params = new URLSearchParams({
        skip_duplicates: (options.skip_duplicates !== false).toString(),
        update_duplicates: (options.update_duplicates || false).toString(),
      });

      if (options.column_mapping) {
        params.set('column_mapping', JSON.stringify(options.column_mapping));
      }

      if (options.new_list_name) {
        params.set('new_list_name', options.new_list_name);
      } else if (options.list_id != null) {
        params.set('list_id', options.list_id.toString());
      }

      const response = await api.post(`/contacts/import?${params.toString()}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        // The ImportContactsModal renders its own toast for import errors
        // (e.g. the friendly malformed-file message); skip the interceptor's
        // generic toast to avoid showing it twice.
        skipErrorToast: true,
      } as any);

      set({ loading: false });

      // Refresh data in background — don't block the import result
      Promise.all([
        get().fetchContacts(),
        get().fetchContactCount(),
        get().fetchImportHistory(),
        get().fetchTags(),
        get().fetchContactLists(),
      ]).catch(console.error);

      return response.data;
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to import contacts', loading: false });
      throw error;
    }
  },

  fetchImportHistory: async () => {
    try {
      const response = await api.get('/contacts/imports/history');
      set({ importHistory: response.data });
    } catch (error) {
      console.error('Failed to fetch import history:', error);
    }
  },

  downloadTemplate: async (format: 'csv' | 'xlsx') => {
    try {
      const response = await api.get(`/contacts/import/template?format=${format}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contact_import_template.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download template:', error);
      throw error;
    }
  },

  fetchContactLists: async () => {
    try {
      const response = await api.get('/contacts/lists');
      set({ contactLists: response.data });
    } catch (error) {
      console.error('Failed to fetch contact lists:', error);
    }
  },

  createContactList: async (data) => {
    set({ loading: true, error: null });
    try {
      const response = await api.post('/contacts/lists', data);
      // The backend may return an existing list (same name, case-insensitive)
      // with the contacts merged in — upsert by id rather than duplicating.
      set((state) => ({
        contactLists: state.contactLists.some((l) => l.id === response.data.id)
          ? state.contactLists.map((l) => (l.id === response.data.id ? response.data : l))
          : [response.data, ...state.contactLists],
        loading: false,
      }));
      return response.data;
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to create list', loading: false });
      throw error;
    }
  },

  updateContactList: async (id, data) => {
    set({ loading: true, error: null });
    try {
      const response = await api.put(`/contacts/lists/${id}`, data);
      set((state) => ({
        contactLists: state.contactLists.map((l) => (l.id === id ? response.data : l)),
        loading: false,
      }));
      return response.data;
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to update list', loading: false });
      throw error;
    }
  },

  deleteContactList: async (id) => {
    set({ loading: true, error: null });
    try {
      const response = await api.delete(`/contacts/lists/${id}`);
      if (response.data?.status === 'pending_approval') {
        // A Representative's delete is a request: the list stays.
        set({ loading: false });
        return response.data as PendingDeletionResult;
      }
      set((state) => ({
        contactLists: state.contactLists.filter((l) => l.id !== id),
        loading: false,
      }));
    } catch (error: any) {
      // Error toast is shown globally by the axios response interceptor.
      const detail = error.response?.data?.detail || 'Failed to delete list';
      set({ error: detail, loading: false });
      throw error;
    }
  },

  reset: () => set(initialState),
}));
